import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl } from '../../util/reactIntl';
import { parse } from '../../util/urlHelpers';
import routeConfiguration from '../../routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import {
  DatePicker,
  LayoutSideNavigation,
  LayoutWrapperMain,
  LayoutWrapperBusinessSideNav,
  LayoutWrapperTopbar,
  LayoutWrapperFooter,
  Footer,
  Page,
  UserNav,
  Select
} from '../../components';
import { TopbarContainer } from '..';
import config from '../../config';

import { isScrollingDisabled } from '../../ducks/UI.duck';
import css from './BusinessUtilizationPage.css';

const toIsoLocal = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const today = new Date();

const yesterday = new Date(
  today.getFullYear(),
  today.getMonth(),
  today.getDate() - 1
);

const sameDayLastYear = new Date(
  yesterday.getFullYear() - 1,
  yesterday.getMonth(),
  yesterday.getDate() + 2
);

const sameDayLastYearISO = toIsoLocal(sameDayLastYear);

const countryData = [
  { value: 'Polska (PLN)', countryId: 'pl', currency: 'PLN' },
  { value: 'Magyarország (HUF)', countryId: 'hu', currency: 'HUF' },
  { value: 'Česká republika (CZK)', countryId: 'cz', currency: 'CZK' },
  { value: 'Slovensko (EUR)', countryId: 'sk', currency: 'EUR' }
];

export const BusinessForecastPageComponent = props => {
  const {
    scrollingDisabled,
    intl,
    location,
    history,
    onLoadData,
    currentUser
  } = props;

  const countryUser = countryData.find(({ countryId }) => countryId === currentUser?.attributes?.language);
  const defaultCurrency = countryUser ? countryUser.currency : 'PLN';
  const defaultCountryId = config.custom.countryId;

  const {
    start = toIsoLocal(today),
    end = toIsoLocal(today),
    prev_start = sameDayLastYearISO,
    prev_end = sameDayLastYearISO,
    currency = defaultCurrency,
    countryId = defaultCountryId,
    isDecathlon = true,
    category = 'bikes_gravel'
  } = parse(location.search);

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    
    onLoadData({ 
      date: start, 
      countryId, 
      businessCategory: category, 
      providerId: '*', 
      include: ['provider'] 
    })
    .then((res) => {
      if (!res) {
        setData([]);
        setIsLoading(false);
        return;
      }
  
      let rawData = [];
      let includedProviders = [];
  
      if (res.data && res.data.data && Array.isArray(res.data.data)) {
        rawData = res.data.data;
        includedProviders = res.data.included || [];
      } else if (res.data && Array.isArray(res.data)) {
        rawData = res.data;
        includedProviders = res.included || [];
      } else if (res.included && Array.isArray(res.data)) {
        rawData = res.data;
        includedProviders = res.included;
      }
  
      if (Array.isArray(rawData) && rawData.length > 0) {
        const formattedData = rawData.map(item => {
          // Pierwszy rekord z Twojego API ma id "all" – to jest agregat globalny
          if (item.id === 'all') {
            return {
              id: 'all',
              name: 'Suma (Wszystkie)',
              managed: item.attributes?.managed || 0,
              booked: item.attributes?.booked || 0,
              available: item.attributes?.available || 0,
              isDecathlon: null
            };
          }
  
          // Wyciągamy ID providera z relacji, dbając o to, czy jest obiektem UUID czy stringiem
          const providerRefId = item.relationships?.provider?.data?.id;
          const providerIdString = providerRefId && typeof providerRefId === 'object' 
            ? providerRefId.uuid 
            : providerRefId;
          
          // Szukamy odpowiedniego providera w included (porównujemy jako proste stringi)
          const fullProvider = includedProviders.find(p => {
            const pIdString = p.id && typeof p.id === 'object' ? p.id.uuid : p.id;
            return String(pIdString) === String(providerIdString) && p.type === 'provider';
          });
  
          // Bezpiecznie pobieramy nazwę (sprawdzamy name, secondaryName w attributes oraz bezpośrednio)
          const attributes = item.attributes || item;
          const provAttributes = fullProvider?.attributes || fullProvider;
          
          const shopName = provAttributes 
            ? (provAttributes.name || provAttributes.secondaryName || 'Nieznany sklep') 
            : null;
  
          return {
            id: item.id || Math.random().toString(),
            name: shopName,
            managed: attributes.managed || 0,
            booked: attributes.booked || 0,
            available: attributes.available || 0,
            isDecathlon: provAttributes ? !!provAttributes.isDecathlon : null
          };
        });
  
        setData(formattedData.filter(s => s.name).sort((a,b) => b.managed - a.managed));
      } else {
        setData([]);
      }
      
      setIsLoading(false);
    })
    .catch((err) => {
      console.error('Błąd podczas ładowania danych:', err);
      setData([]);
      setIsLoading(false);
    });
  }, [start, end, prev_start, prev_end, currency, countryId, isDecathlon, category, onLoadData]);

  const title = intl.formatMessage({ id: "BusinessForecastPage.title" });

  const handleDateRangeSubmit = ({ start, end }) => {
    history.push(
      createResourceLocatorString(
        'BusinessUtilizationPage',
        routeConfiguration(),
        {},
        { start, end, currency, countryId, isDecathlon, category }
      )
    );
  };

  const handleCurrencyChange = (e) => {
    const country = countryData.find(c => c.countryId === e.target.value);
    if (!country) return;

    history.push(
      createResourceLocatorString(
        'BusinessUtilizationPage',
        routeConfiguration(),
        {},
        { currency: country.currency.toUpperCase(), countryId: country.countryId, start, end, isDecathlon, category }
      )
    );
  };

  const handleCategoryChange = (e) => {
    history.push(
      createResourceLocatorString(
        'BusinessUtilizationPage',
        routeConfiguration(),
        {},
        { category: e.target.value, currency, countryId, start, end, isDecathlon }
      )
    );
  };

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSideNavigation>
        <LayoutWrapperTopbar>
          <TopbarContainer
            currentPage="BusinessDktPage"
            desktopClassName={css.desktopTopbar}
            mobileClassName={css.mobileTopbar}
          />
          <UserNav selectedPageName="BusinessDktPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperBusinessSideNav currentTab="BusinessDktPage" />
        <LayoutWrapperMain className={css.colorBackground}>
          <div className={css.content}>
            <div className={css.header}>
              <h1 className={css.title}>
                {intl.formatMessage({ id: 'BusinessDktPage.yourResults' })}
              </h1>
              <div>
                <DatePicker
                  onSubmit={handleDateRangeSubmit}
                  value={{ startDate: start, endDate: end, prevStartDate: prev_start, prevEndDate: prev_end }}
                  futureDatesOn
                />
              </div>
            </div>
            
            <div className={css.filters}>
              <Select
                label={intl.formatMessage({ id: 'BusinessDktPage.country' })}
                value={countryId}
                onChange={handleCurrencyChange}
              >
                {countryData.map(({ value, countryId }) => (
                  <option key={countryId} value={countryId}>{value}</option>
                ))}
              </Select>
              
              <Select
                label="Kategoria"
                value={category}
                onChange={handleCategoryChange}
              >
                <option value='bikes_gravel'>bikes_gravel</option>
                <option value='bikes_electric'>bikes_electric</option>
                <option value='bikes_road'>bikes_road</option>
                <option value='bikes_mtb'>bikes_mtb</option>
                <option value='bikes_trekking'>bikes_trekking</option>
                <option value='camping_tent'>camping_tent</option>
                <option value='camping_rooftent'>camping_rooftent</option>
                <option value='watersports_sup'>watersports_sup</option>
                <option value='watersports_kayak'>watersports_kayak</option>
              </Select>
            </div>
            <div className={css.resultsContainer}>
              {isLoading ? (
                <p className={css.loadingText}>Ładowanie danych...</p>
              ) : data && data.length > 0 ? (
                <table className={`${css.resultsTable} ${css.utilizationPageTable}`}>
                  <thead>
                    <tr>
                      <th>Lokalizacja / Sklep</th>
                      <th>Zarządzane</th>
                      <th>Zarezerwowane</th>
                      <th>Dostępne</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map(row => (
                      <tr key={row.id} className={row.id === 'all' ? css.summaryRow : ''}>
                        <td><strong>{row.name}</strong></td>
                        <td>{row.managed}</td>
                        <td>{row.booked}</td>
                        <td>{row.available}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className={css.noDataText}>Brak danych dla wybranych filtrów.</p>
              )}
            </div>

          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSideNavigation>
    </Page>
  );
};

BusinessForecastPageComponent.defaultProps = {
  saveEmailError: null,
  savePhoneNumberError: null,
  currentUser: null,
  sendVerificationEmailError: null,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  return {
    currentUser,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const loadData = params => (dispatch, getState, sdk) => {
  return sdk.newSdk.analyticsAvailabilities.query({ ...params })
    .then((res) => {
      return res;
    })
    .catch(e => {
      console.error(e, 'initial-message-send-failed', { tx: 'PLN' });
      throw e; 
    });
};

const mapDispatchToProps = dispatch => ({
  onLoadData: (params) => dispatch(loadData(params))
});

const BusinessForecastPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl,
  withRouter
)(BusinessForecastPageComponent);

export default BusinessForecastPage;